import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { R, shadow } from '../lib/ui';
import { label as labelStyle } from '../lib/ui';
import type { MenuEntry } from './ContextMenu';
import { AnchoredPopover } from './AnchoredPopover';

/** Пункт меню в выпадающем списке шапки. */
function MenuRow({ item, onSelect }: { item: MenuEntry; onSelect: () => void }) {
  const [open, setOpen] = useState(false);

  if ('type' in item && item.type === 'divider') {
    return <div style={{ height: 1, background: R.grid, margin: '6px 0' }} />;
  }

  if ('type' in item && item.type === 'header') {
    return <div style={{ ...labelStyle, padding: '6px 12px 8px' }}>{item.label}</div>;
  }

  const entry = item;
  const color = entry.disabled ? R.textMuted : entry.danger ? R.danger : R.text;

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        disabled={entry.disabled}
        onClick={() => {
          if (entry.disabled || entry.submenu) return;
          entry.onClick?.();
          onSelect();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '7px 12px',
          textAlign: 'left',
          fontSize: 13,
          color,
          opacity: entry.disabled ? 0.5 : 1,
          cursor: entry.disabled ? 'default' : 'pointer',
          background: 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!entry.disabled) e.currentTarget.style.background = R.bgElevated;
        }}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {entry.icon && <entry.icon size={14} />}
        <span style={{ flex: 1 }}>{entry.label}</span>
        {entry.shortcut && (
          <span style={{ fontSize: 11.5, color: R.textMuted }}>{entry.shortcut}</span>
        )}
        {entry.submenu && <ChevronRight size={13} color={R.textMuted} />}
      </button>

      {entry.submenu && open && (
        <div
          style={{
            position: 'absolute',
            top: -6,
            left: '100%',
            minWidth: 190,
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            boxShadow: shadow.dropdown,
            padding: '6px 0',
            zIndex: 320,
          }}
        >
          {entry.submenu.map((sub, i) => (
            <MenuRow key={i} item={sub} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Кнопка меню в шапке документа: «Файл», «Данные». */
export function MenuButton({
  label, items, disabled,
}: {
  label: string;
  items: MenuEntry[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={ref}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: 28,
          padding: '0 10px',
          fontSize: 13,
          color: disabled ? R.textMuted : R.text,
          background: open ? R.bgElevated : 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}
        onMouseEnter={(e) => {
          if (!disabled && !open) e.currentTarget.style.background = R.bgElevated;
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent';
        }}
      >
        <span>{label}</span>
        <ChevronDown size={11} strokeWidth={2} color={R.textMuted} />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          style={{
            minWidth: 230,
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            boxShadow: shadow.dropdown,
            padding: '6px 0',
          }}
        >
          {items.map((item, i) => (
            <MenuRow key={i} item={item} onSelect={() => setOpen(false)} />
          ))}
        </div>
      </AnchoredPopover>
    </>
  );
}

/** Текст, который по двойному клику превращается в поле ввода. */
export function InlineEdit({
  value, onSave, disabled, style, editStyle, placeholder,
}: {
  value: string;
  onSave: (value: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
  editStyle?: CSSProperties;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    setDraft(value);
    queueMicrotask(() => {
      ref.current?.focus();
      ref.current?.select();
    });
  }, [editing, value]);

  const commit = () => {
    if (draft.trim() && draft.trim() !== value) onSave(draft.trim());
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value);
            setEditing(false);
          }
        }}
        style={{
          padding: '2px 6px',
          background: R.bgInput,
          border: `1px solid ${R.accent}`,
          color: R.text,
          ...editStyle,
        }}
      />
    );
  }

  return (
    <span
      onDoubleClick={() => !disabled && setEditing(true)}
      title={disabled ? undefined : 'Дважды кликните, чтобы переименовать'}
      style={{ cursor: disabled ? 'default' : 'text', ...style }}
    >
      {value || placeholder}
    </span>
  );
}
